import { Trans } from "@lingui/react/macro";
import { fetchCoordinates } from "@/api/Mapbox";
import { patientKeys, updateAddress } from "@/api/Patients";
import Form from "@/components/Form/Form";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deducedError } from "@/Utils/ErrorUtils";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { MapProvider, useMap } from "react-map-gl/mapbox";
import { FilledButton } from "@medoma/components/Button/Button";
import { useCallback, useRef } from "react";
import type { ICoordinatePair } from "@/components/Map/Map";
import {
  formatAddress,
  type IAddressWithCoordinates,
} from "@medoma/models/addresses";
import { useState, useEffect } from "react";
import InputField from "@medoma/components/InputField/InputField";
import styles from "./EditAddress.module.scss";
import { useLingui } from "@lingui/react/macro";
import { useCareHub } from "@/api/Patients";
import { getValidCoordinates } from "./coordinatesUtils";
import {
  conventionalPostalCodePattern,
  conventionalPostalCodeSchema,
  internalizePostalCode,
} from "@medoma/models/postalCode";
import { useUserProfile } from "@/api/Users";
import { PatientMap } from "@/pages/commandcenter/Patients/Patient/Information/PatientMap";

export const EditAddress = ({
  currentAddress,
  onSuccess,
  patientId,
}: {
  currentAddress: StrictOmit<IAddressWithCoordinates, "addressLine2">;
  onSuccess: () => void;
  patientId: string;
}) => {
  return (
    <MapProvider>
      <InnerEditAddress
        currentAddress={currentAddress}
        onSuccess={onSuccess}
        patientId={patientId}
      />
    </MapProvider>
  );
};

const InnerEditAddress = ({
  currentAddress,
  onSuccess,
  patientId,
}: {
  currentAddress: StrictOmit<IAddressWithCoordinates, "addressLine2">;
  onSuccess: () => void;
  patientId: string;
}) => {
  const {
    formState: { errors, dirtyFields, isDirty },
    getValues,
    handleSubmit,
    register,
    setError,
    setValue,
    reset,
    trigger,
    watch,
  } = useForm<IAddressWithCoordinates>({
    defaultValues: {
      ...currentAddress,
    },
  });

  const PREFERRED_EDIT_ADDRESS_ZOOM = 13;
  const MAX_DISTANCE_FROM_CARE_HUB_IN_METERS = 150_000;

  const queryClient = useQueryClient();
  const { data: careHub } = useCareHub();
  const isFirstRender = useRef(true);

  const { mutate, isPending, isSuccess } = useMutation({
    mutationFn: (address: IAddressWithCoordinates) =>
      updateAddress(patientId, address),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: patientKeys.detail(patientId),
      });
      onSuccess();
    },
  });

  const validateAndSubmit = handleSubmit((formData) => {
    const postalCode = conventionalPostalCodeSchema.shape[countryCode].parse(
      formData.postalCode,
    );
    mutate(formatAddress({ ...formData, countryCode, postalCode }));
  });

  const setCoordinates = useCallback(
    (coordinates: ICoordinatePair) => setValue("coordinates", coordinates),
    [setValue],
  );

  const { registerPatientMap } = useMap();

  const triggerCoordinateValidation = useCallback(
    () => trigger(["coordinates.latitude", "coordinates.longitude"]),
    [trigger],
  );

  // Acceptable. We don't use react compiler, we just lint to find incompatibilities, such as this.
  // oxlint-disable-next-line react/incompatible-library
  const { addressLine1, postalCode, city, coordinates } = watch();
  const areCoordinatesDirty = Boolean(
    dirtyFields.coordinates?.latitude || dirtyFields.coordinates?.longitude,
  );
  const coordinatesProvided =
    Boolean(coordinates.latitude && coordinates.longitude) ||
    areCoordinatesDirty;

  const [coordinatesFailedToFetch, setCoordinatesFailedToFetch] =
    useState(false);
  useEffect(() => {
    if (!registerPatientMap) {
      return;
    }
    if (!careHub) {
      return;
    }

    const validCoordinates = getValidCoordinates({
      longitude: coordinates.longitude,
      latitude: coordinates.latitude,
      careHub: careHub,
      maxDistance: MAX_DISTANCE_FROM_CARE_HUB_IN_METERS,
    }) || { longitude: careHub.longitude, latitude: careHub.latitude };

    if (validCoordinates) {
      registerPatientMap.easeTo({
        center: [validCoordinates.longitude, validCoordinates.latitude],
        duration: 1000,
        zoom: PREFERRED_EDIT_ADDRESS_ZOOM,
      });
    }
  }, [
    registerPatientMap,
    coordinates.latitude,
    coordinates.longitude,
    careHub,
  ]);

  const { countryCode } = useUserProfile();

  useEffect(() => {
    const handler = setTimeout(() => {
      if (
        addressLine1 &&
        postalCode &&
        conventionalPostalCodeSchema.shape[countryCode].safeParse(postalCode)
          .success
      ) {
        fetchCoordinates({
          addressLine1,
          countryCode,
          postalCode: internalizePostalCode(
            conventionalPostalCodeSchema.shape[countryCode].parse(postalCode),
            countryCode,
          ),
        })
          .then((coordinates) => {
            if (isFirstRender.current) {
              // On the first render, show coordinates based on patient's saved adress
              setCoordinates(currentAddress.coordinates);
              isFirstRender.current = false;
            } else {
              // On subsequent renders, override the patient coordinates with coordinates calculated from new address
              setCoordinates(coordinates);
            }
            setCoordinatesFailedToFetch(false);
            void triggerCoordinateValidation();
          })
          .catch(() => {
            setCoordinatesFailedToFetch(true);
          });
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [
    addressLine1,
    postalCode,
    city,
    setCoordinates,
    triggerCoordinateValidation,
    currentAddress.coordinates,
    countryCode,
  ]);

  const { t } = useLingui();

  return (
    <Form onSubmit={validateAndSubmit}>
      {isPending || isSuccess ? (
        <Loading message={t`Ändrar adressen`} />
      ) : (
        <>
          {errors.root?.server?.message && !isDirty ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}
          <>
            <InputField
              label={t`Gata`}
              showOptionalLabel={false}
              errorMessage={errors.addressLine1?.message}
              {...register("addressLine1", {
                required: { value: true, message: t`Ange gata` },
              })}
            />
            <Form.Row>
              <InputField
                label={t`Postnummer`}
                showOptionalLabel={false}
                errorMessage={errors.postalCode?.message}
                {...register("postalCode", {
                  required: { value: true, message: t`Ange postnummer` },
                  pattern: {
                    value: conventionalPostalCodePattern[countryCode].value,
                    message: t(
                      conventionalPostalCodePattern[countryCode].message,
                    ),
                  },
                })}
              />
              <InputField
                label={t`Postort`}
                showOptionalLabel={false}
                errorMessage={errors.city?.message}
                {...register("city", {
                  required: { value: true, message: t`Ange postort` },
                })}
              />
            </Form.Row>
            <Form.Row>
              <fieldset
                className={styles.fieldset}
                disabled={!coordinatesProvided && !coordinatesFailedToFetch}
              >
                <legend>
                  {!coordinatesProvided && !coordinatesFailedToFetch
                    ? t`Fyll i adress för att se plats`
                    : coordinatesFailedToFetch
                      ? t`Kunde inte ladda kartan. Fyll i koordinater manuellt.`
                      : t`Plats`}
                </legend>
                <div className={styles.mapAndCoordinatesSection}>
                  <PatientMap
                    coordinates={coordinates}
                    style={{ height: "30vh" }}
                    liveEdit={{ areCoordinatesDirty, coordinatesFailedToFetch }}
                    allowScrollZoom={false} // Disable scroll zoom to not accidentally zoom when scrolling
                  />
                  <div className={styles.coordinateSection}>
                    <Form.Row>
                      <InputField
                        label={t`Longitud`}
                        showOptionalLabel={false}
                        errorMessage={errors.coordinates?.longitude?.message}
                        {...register("coordinates.longitude", {
                          required: {
                            value: true,
                            message: t`Longitud behöver anges`,
                          },
                          min: {
                            value: -180,
                            message: t`Longitud bör vara över -180`,
                          },
                          max: {
                            value: 180,
                            message: t`Longitud bör vara under 180`,
                          },
                        })}
                      />
                      <InputField
                        label={t`Latitud`}
                        showOptionalLabel={false}
                        errorMessage={errors.coordinates?.latitude?.message}
                        {...register("coordinates.latitude", {
                          required: {
                            value: true,
                            message: t`Latitud behöver anges`,
                          },
                          min: {
                            value: -90,
                            message: t`Latitud bör vara över -90`,
                          },
                          max: {
                            value: 90,
                            message: t`Latitud bör vara under 90`,
                          },
                        })}
                      />
                    </Form.Row>
                    {coordinatesProvided ? (
                      <p>
                        <Trans>
                          Fyll i koordinater manuellt om kartnålen är
                          felplacerad
                        </Trans>
                      </p>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </fieldset>
            </Form.Row>
          </>
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        </>
      )}
    </Form>
  );
};
